import { useState, useEffect } from 'react';
import api, { formatError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Megaphone, Plus, AlertTriangle, BellRing } from 'lucide-react';

export default function NoticesPage() {
  const { user } = useAuth();
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pushDialogOpen, setPushDialogOpen] = useState(false);
  const [pushForm, setPushForm] = useState({ title: '', body: '', url: '/investor', target: 'investor' });
  const [pushSending, setPushSending] = useState(false);
  const [form, setForm] = useState({ title: '', message: '', priority: 'normal', target_role: '', target_branch: '' });
  const isAdmin = ['super_admin', 'admin'].includes(user?.role);

  useEffect(() => {
    api.get('/notices').then(res => setNotices(res.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSend = async () => {
    try {
      await api.post('/notices', form);
      toast.success('Notice sent to employees');
      setDialogOpen(false);
      setForm({ title: '', message: '', priority: 'normal', target_role: '', target_branch: '' });
      const res = await api.get('/notices');
      setNotices(res.data);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const handleSendPushBroadcast = async () => {
    if (!pushForm.title.trim() || !pushForm.body.trim()) {
      toast.error('Title and body are required');
      return;
    }
    setPushSending(true);
    try {
      const role_filter = pushForm.target === 'all' ? null : [pushForm.target];
      const { data } = await api.post('/push/broadcast', {
        title: pushForm.title,
        body: pushForm.body,
        url: pushForm.url || '/investor',
        role_filter,
      });
      toast.success(`Push sent to ${data?.sent || 0} device(s) across ${data?.users || 0} user(s)`);
      setPushDialogOpen(false);
      setPushForm({ title: '', body: '', url: '/investor', target: 'investor' });
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setPushSending(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="notices-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-brand-text-primary tracking-tight">Notices</h1>
          <p className="text-sm text-brand-text-secondary mt-1">Announcements and employee notices</p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <Button
              onClick={() => setPushDialogOpen(true)}
              variant="outline"
              className="border-emerald-500 text-emerald-700 hover:bg-emerald-50 rounded-sm"
              data-testid="broadcast-push-btn"
            >
              <BellRing size={16} className="mr-2" /> Broadcast Push
            </Button>
            <Button onClick={() => setDialogOpen(true)} className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm" data-testid="send-notice-btn">
              <Plus size={16} className="mr-2" /> Send Notice
            </Button>
          </div>
        )}
      </div>

      <div className="space-y-3">
        {loading ? <p className="text-brand-text-secondary text-sm">Loading...</p> :
         notices.length === 0 ? (
           <Card className="border border-brand-border bg-white"><CardContent className="p-12 text-center text-brand-text-secondary">No notices yet</CardContent></Card>
         ) : notices.map(n => (
          <Card key={n.id} className="border border-brand-border bg-white hover:shadow-sm transition-shadow">
            <CardContent className="p-5">
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${n.priority === 'high' ? 'bg-brand-error/10' : 'bg-brand-primary/10'}`}>
                  {n.priority === 'high' ? <AlertTriangle size={18} className="text-brand-error" /> : <Megaphone size={18} className="text-brand-primary" />}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-sm font-semibold text-brand-text-primary">{n.title}</h3>
                    {n.priority === 'high' && <Badge className="text-[10px] bg-brand-error/15 text-brand-error border-0">High Priority</Badge>}
                  </div>
                  <p className="text-sm text-brand-text-secondary leading-relaxed">{n.message}</p>
                  <div className="flex items-center gap-3 mt-2 text-xs text-brand-text-secondary/60">
                    <span>By {n.created_by_name}</span>
                    <span>{n.created_at ? new Date(n.created_at).toLocaleString() : ''}</span>
                    {n.target_role && <Badge className="text-[10px] bg-brand-bg border-0 text-brand-text-secondary">{n.target_role}</Badge>}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="notice-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Send Notice</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2"><Label>Title *</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="notice-title" className="bg-white border-brand-border rounded-sm" /></div>
            <div className="space-y-2"><Label>Message *</Label><Textarea value={form.message} onChange={e => setForm({...form, message: e.target.value})} rows={4} data-testid="notice-message" className="bg-white border-brand-border rounded-sm" /></div>
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={v => setForm({...form, priority: v})}>
                <SelectTrigger className="bg-white border-brand-border rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High Priority</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Target Role (optional)</Label>
              <Select value={form.target_role} onValueChange={v => setForm({...form, target_role: v})}>
                <SelectTrigger className="bg-white border-brand-border rounded-sm"><SelectValue placeholder="All employees" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value=" ">All Employees</SelectItem>
                  <SelectItem value="manager">Managers</SelectItem>
                  <SelectItem value="trader">Traders</SelectItem>
                  <SelectItem value="office_staff">Office Staff</SelectItem>
                  <SelectItem value="receptionist">Receptionists</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-brand-border rounded-sm">Cancel</Button>
            <Button onClick={handleSend} className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm" data-testid="notice-send-btn">Send Notice</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={pushDialogOpen} onOpenChange={setPushDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="push-broadcast-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <BellRing className="w-5 h-5 text-emerald-600" /> Broadcast Push
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-xs text-brand-text-secondary -mt-2">
              Instantly pop a native push notification on every subscribed device. Only reaches users who have enabled alerts.
            </p>
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                maxLength={120}
                value={pushForm.title}
                onChange={e => setPushForm({ ...pushForm, title: e.target.value })}
                placeholder="New Fund Launch 🚀"
                data-testid="push-title-input"
                className="bg-white border-brand-border rounded-sm"
              />
            </div>
            <div className="space-y-2">
              <Label>Message *</Label>
              <Textarea
                rows={3}
                maxLength={400}
                value={pushForm.body}
                onChange={e => setPushForm({ ...pushForm, body: e.target.value })}
                placeholder="Our Quantora Alpha Fund is live. Tap to invest now."
                data-testid="push-body-input"
                className="bg-white border-brand-border rounded-sm"
              />
            </div>
            <div className="space-y-2">
              <Label>Audience</Label>
              <Select value={pushForm.target} onValueChange={v => setPushForm({ ...pushForm, target: v })}>
                <SelectTrigger className="bg-white border-brand-border rounded-sm" data-testid="push-audience-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="investor">All Investors</SelectItem>
                  <SelectItem value="trader">All Traders</SelectItem>
                  <SelectItem value="manager">All Managers</SelectItem>
                  <SelectItem value="all">All Users (everyone)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Open URL on tap</Label>
              <Input
                value={pushForm.url}
                onChange={e => setPushForm({ ...pushForm, url: e.target.value })}
                placeholder="/investor"
                className="bg-white border-brand-border rounded-sm"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPushDialogOpen(false)} className="border-brand-border rounded-sm">Cancel</Button>
            <Button
              onClick={handleSendPushBroadcast}
              disabled={pushSending}
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm"
              data-testid="push-send-btn"
            >
              <BellRing size={16} className="mr-2" />{pushSending ? 'Sending…' : 'Send Push'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
